import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Flame, Mail, AlertCircle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardDescription } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

// Lünefire Logo Component
const LunefireLogo = ({ size = "lg" }) => {
  const sizes = {
    md: { flame: "w-10 h-10", text: "text-2xl" },
    lg: { flame: "w-14 h-14", text: "text-3xl" }
  };
  const s = sizes[size] || sizes.lg;
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <Flame className={`${s.flame} text-[#DC2626]`} strokeWidth={2} />
        <Flame className={`${s.flame} text-[#FF8C00] absolute top-0 left-0 opacity-60`} strokeWidth={2} style={{ transform: 'scale(0.7) translate(20%, 30%)' }} />
      </div>
      <div className="text-center">
        <span className={`font-heading font-bold ${s.text} lunefire-gradient-text`}>Lünefire</span>
        <span className="block text-xs text-slate-500 tracking-widest">SNACKSTATION</span>
      </div>
    </div>
  );
};

// Password Change Dialog
const PasswordChangeDialog = ({ onSuccess, onCancel }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { token } = useAuth();

  const requirements = [
    { met: newPassword.length >= 8, text: "Mindestens 8 Zeichen" },
    { met: /[A-Z]/.test(newPassword), text: "Mindestens ein Großbuchstabe" },
    { met: /[0-9]/.test(newPassword), text: "Mindestens eine Zahl" },
    { met: /[!@#$%^&*()_+\-=\[\]{};:,.<>?]/.test(newPassword), text: "Mindestens ein Sonderzeichen" },
    { met: newPassword && newPassword === confirmPassword, text: "Passwörter stimmen überein" }
  ];

  const allMet = requirements.every(r => r.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allMet) return;

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Passwort erfolgreich geändert");
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Ändern des Passworts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="text-center mb-6">
          <Lock className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h2 className="text-xl font-bold text-slate-800">Passwort ändern</h2>
          <p className="text-slate-500 text-sm mt-1">
            Sie müssen Ihr Passwort bei der ersten Anmeldung ändern
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Altes Passwort</label>
            <Input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              className="mt-1"
              autoFocus
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Neues Passwort</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Neues Passwort bestätigen</label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-medium text-slate-600">Anforderungen:</p>
            {requirements.map((req, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs ${req.met ? 'text-emerald-600' : 'text-slate-400'}`}>
                {req.met ? <Check className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-current" />}
                {req.text}
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
              Abmelden
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]"
              disabled={!allMet || loading}
            >
              {loading ? "Speichern..." : "Passwort ändern"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const LoginPage = () => {
  const [loginMode, setLoginMode] = useState("user"); // "user" or "legacy"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const { login, isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (isAuthenticated && user?.must_change_password) {
      setShowPasswordChange(true);
    } else if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loginMode === "user" && (!email.trim() || !password.trim())) return;
    if (loginMode === "legacy" && !password.trim()) return;
    
    setLoading(true);
    const success = await login(password, loginMode === "user" ? email : null);
    setLoading(false);
    
    if (success) {
      // Check for must_change_password is handled by useEffect
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordChange(false);
    navigate(from, { replace: true });
  };

  const handlePasswordChangeCancel = () => {
    logout();
    setShowPasswordChange(false);
  };

  if (showPasswordChange) {
    return <PasswordChangeDialog onSuccess={handlePasswordChangeSuccess} onCancel={handlePasswordChangeCancel} />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mb-4">
                <LunefireLogo size="lg" />
              </div>
              <CardDescription className="text-base">
                Feuerwehr Lüneburg
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Login Mode Toggle */}
              <div className="flex rounded-lg bg-slate-100 p-1 mb-6">
                <button
                  type="button"
                  onClick={() => setLoginMode("user")}
                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition ${
                    loginMode === "user" 
                      ? "bg-white shadow text-slate-800" 
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Benutzer-Login
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMode("legacy")}
                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition ${
                    loginMode === "legacy" 
                      ? "bg-white shadow text-slate-800" 
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Schnell-Login
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {loginMode === "user" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      E-Mail-Adresse
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@feuerwehr.de"
                        className="pl-10 h-12"
                        data-testid="email-input"
                        autoFocus={loginMode === "user"}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Passwort
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      data-testid="password-input"
                      autoFocus={loginMode === "legacy"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase tracking-wide btn-hover"
                  disabled={loading || (loginMode === "user" ? (!email.trim() || !password.trim()) : !password.trim())}
                  data-testid="login-btn"
                >
                  {loading ? "Anmelden..." : "Anmelden"}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                {loginMode === "user" 
                  ? "Melden Sie sich mit Ihrem Benutzerkonto an"
                  : "Schneller Zugang mit Gruppen-Passwort"}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right side - Gradient (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative lunefire-gradient">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center">
            <Flame className="w-24 h-24 mx-auto mb-6 drop-shadow-lg" />
            <h2 className="font-heading text-4xl font-bold mb-4 drop-shadow-lg">
              Immer einsatzbereit
            </h2>
            <p className="text-lg text-white/90 max-w-md">
              Verwalten Sie den Snackautomaten der Feuerwehr Lüneburg effizient und unkompliziert.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
